import { Metric, SupportedAccountType } from "@front/m19-metrics";
import { AdStatsEx, InventoryStats, OrderStats, ProductEx } from "@front/m19-models";

export enum WidgetType {
  ScoreCard = "scoreCard",
  LineChart = "lineChart",
  DataTable = "dataTable",
  DonutChart = "donutChart",
  BarChart = "barChart",
  WeeklyStats = "kpi",
  VariationTable = "variationTable",
  ProfitGraph = "profitGraph",
  ProductView = "productView",
  ParentAsinStats = "parentAsinStats",
}

export enum ViewBy {
  GLOBAL,
  ASINS,
  PARENT_ASINS,
  PRODUCT_TAG_1,
  PRODUCT_TAG_2,
  BRANDED_TRAFFIC,
  STRATEGIES,
  PRODUCT_BRANDS,
  PROFIT_DETAILS,
  PRODUCT_EVENT_TIMELINE,
  SEARCH_TERM_ADVERTISING_DATA,
  TARGETED_PRODUCTS_ADVERTISING_DATA,
  SEARCH_TERM_RANKINGS,
  FAMILY_STATS,
}

export function viewByFormat(viewBy: ViewBy, productTag1Name?: string, productTag2Name?: string): string {
  switch (viewBy) {
    case ViewBy.GLOBAL:
      return "sov-pie-chart.global";
    case ViewBy.ASINS:
      return "common.asins";
    case ViewBy.PARENT_ASINS:
      return "widget.modelparent_asins";
    case ViewBy.PRODUCT_TAG_1:
      return productTag1Name ?? "custom-field-edition.custom_field_1";
    case ViewBy.PRODUCT_TAG_2:
      return productTag2Name ?? "custom-field-edition.custom_field_2";
    case ViewBy.BRANDED_TRAFFIC:
      return "widget.modelbranded_traffic";
    case ViewBy.STRATEGIES:
      return "common.strategies";
    case ViewBy.PRODUCT_BRANDS:
      return "widget.modelproduct_brands";
    case ViewBy.PROFIT_DETAILS:
      return "widget.modelprofit_details";
    case ViewBy.PRODUCT_EVENT_TIMELINE:
      return "product360-widget-service.product_event_timeline";
    case ViewBy.SEARCH_TERM_ADVERTISING_DATA:
      return "product360-widget-service.search_term_advertising_data";
    case ViewBy.TARGETED_PRODUCTS_ADVERTISING_DATA:
      return "product360-widget-service.targeted_products_advertising_data";
    case ViewBy.SEARCH_TERM_RANKINGS:
      return "product360-widget-service.search_terms_rankings";
    case ViewBy.FAMILY_STATS:
      return "product360-widget-service.family_stats";
  }
}

export type MetricColumn = { id: string; hide: boolean };

export enum VariationTableSortBy {
  METRIC_VALUE = "metricValue",
  METRIC_SHARE = "metricShare",
  DELTA = "delta",
  DELTA_PERCENT = "deltaPercent",
}

export enum LineChartDataset {
  AccountStats = "accountStats",
  BrandStats = "brandStats",
}

export enum DataTableDataset {
  ProfitDetails = "profitDetails",
  ProductEventTimeline = "productEventTimeline",
  SearchTermAdvertisingData = "searchTermAdvertisingData",
  TargetedProductsAdvertisingData = "targetedProductsAdvertisingData",
  SearchTermsRankings = "searchTermsRankings",
  FamilyStats = "familyStats",
  BestSellerAsins = "bestSellerAsins",
  StrategyStats = "strategyStats",
  BrandingTrafficTable = "brandingTraffic",
  CustomFieldStats = "customFieldStats",
  ParentAsins = "parentAsins",
  ProductBrands = "productBrands",
}

export enum DonutDataset {
  AdStatsPerCampaignType = "adStatsPerCampaignType",
  BrandingTraffic = "brandingTraffic",
  StatsPerCustomField = "statsPerCustomField",
}

export type WidgetTypeDescription = {
  title: string;
  description: string;
  w: number;
  h: number;
  unique: boolean;
  configurable: boolean;
  iconUrl: string;
  typeIconUrl?: string;
  supportedAccountType?: SupportedAccountType;
};

export const WidgetTypeDescriptions: Record<WidgetType, WidgetTypeDescription> = {
  [WidgetType.ScoreCard]: {
    title: "widget-model.scorecard",
    description: "widget-model.scorecard_description",
    w: 2,
    h: 1,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/score-card.svg",
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [WidgetType.LineChart]: {
    title: "widget-model.line_chart",
    description: "widget-model.line_chart_description",
    w: 6,
    h: 4,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/line-chart.svg",
    typeIconUrl: "assets/img/widget/type-icon/line-chart-type.svg",
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [WidgetType.DataTable]: {
    title: "widget-model.data_table",
    description: "widget-model.data_table_description",
    w: 6,
    h: 4,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/data-table.svg",
    typeIconUrl: "assets/img/widget/type-icon/table-type.svg",
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [WidgetType.DonutChart]: {
    title: "widget-model.donut_chart",
    description: "widget-model.donut_chart_description",
    w: 4,
    h: 4,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/donut-chart.svg",
    typeIconUrl: "assets/img/widget/type-icon/donut-chart-type.svg",
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [WidgetType.BarChart]: {
    title: "widget-model.bar_chart",
    description: "widget-model.bar_chart_description",
    w: 5,
    h: 5,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/bar-chart.svg",
    typeIconUrl: "assets/img/widget/type-icon/bar-chart-type.svg",
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [WidgetType.WeeklyStats]: {
    title: "widget-model.weekly_stats_table",
    description: "widget-model.weekly_stats_table_description",
    w: 6,
    h: 4,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/data-table.svg",
    typeIconUrl: "assets/img/widget/type-icon/table-type.svg",
    supportedAccountType: SupportedAccountType.VENDOR_AND_SELLER,
  },
  [WidgetType.ProfitGraph]: {
    title: "widget-model.profit_analytics_chart",
    description: "widget-model.profit_analytics_chart_description",
    w: 6,
    h: 4,
    unique: false,
    configurable: true,
    iconUrl: "assets/img/widget/profit-chart.svg",
    typeIconUrl: "assets/img/widget/type-icon/line-chart-type.svg",
    supportedAccountType: SupportedAccountType.SELLER,
  },
  [WidgetType.VariationTable]: {
    title: "account360.variation_table",
    description: "widget-model.variation_table_description",
    w: 6,
    h: 4,
    unique: true,
    configurable: true,
    iconUrl: "assets/img/widget/data-table.svg",
    typeIconUrl: "assets/img/widget/type-icon/table-type.svg",
  },
  [WidgetType.ProductView]: {
    title: "product360-widget-service.product_view",
    description: "widget-model.product_view_description",
    w: 12,
    h: 4,
    unique: true,
    configurable: false,
    iconUrl: "assets/img/widget/product-view.svg",
  },
  [WidgetType.ParentAsinStats]: {
    title: "product360-widget-service.parent_asin_stats",
    description: "widget-model.parent_asin_stats_description",
    w: 6,
    h: 4,
    unique: true,
    configurable: true,
    iconUrl: "assets/img/widget/parent-asin-stats.svg",
  },
};

export type Metric360Ex = {
  metric: Metric<AdStatsEx> | Metric<OrderStats> | Metric<ProductEx> | Metric<InventoryStats>;
  category: string;
  supported: boolean;
};
